import * as React from 'react';
import { Box, Paper } from '@mui/material';
import {
  DataGrid,
  DataGridProps,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
} from '@mui/x-data-grid';

import FGridToolbar from './FGridToolbar';

declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides {
    addRow: Function;
    saveAll?: Function;
    saving?: boolean;
    total?: number;
    [name:string]: any;
  }
}

type Props = Omit<DataGridProps, 'rows'> & {
  rows: any[];
  onRowsChange?: (rows: any[]) => void;
  onSubmit?: (rows: any[]) => Promise<void>;
  title?: string;
  subtitle?: string;
  height?: number | string;
  addRow?: () => void;
  saveAll?: () => void;
  toolbarSx?: any;
  showQuickFilter?: boolean;
};

type State = {
  rowModesModel: GridRowModesModel;
  saving: boolean;
};

export class FGrid extends React.Component<Props, State> {
  state: State = {
    rowModesModel: {},
    saving: false,
  };

  getRowKey = (row: any) => row.rid || row.recid || row.id || row.code || row.shortcode;

  processRowUpdate = (newRow: GridRowModel) => {
    const newKey = this.getRowKey(newRow);
    const rows = (this.props.rows || []).map((r: any) =>
      this.getRowKey(r) === newKey
        ? { ...r, ...newRow, _state: (r.rid || r.recid) ? 'dirty' : 'new' }
        : r
    );

    this.props.onRowsChange?.(rows);
    return rows.find((r: any) => this.getRowKey(r) === newKey) || newRow;
  };

  saveAll = async () => {
    if (!this.props.onSubmit) return;

    this.setState({ saving: true });
    try {
      await this.props.onSubmit(this.props.rows);
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const {
      rows,
      onRowsChange,
      onSubmit,
      title,
      subtitle,
      height = 420,
      addRow,
      saveAll,
      toolbarSx,
      showQuickFilter,
      slots,
      slotProps,
      sx,
      ...rest
    } = this.props;

    const actualSaveAll = saveAll || (onSubmit ? this.saveAll : undefined);

    return (
      <Paper elevation={0} sx={{ p: 0, width: '100%' }}>
        <Box sx={{ height, width: '100%' }}>
          <DataGrid
            rows={rows}
            getRowId={this.getRowKey}
            editMode="row"
            rowModesModel={this.props.rowModesModel || this.state.rowModesModel}
            processRowUpdate={this.props.processRowUpdate || this.processRowUpdate}
            //onRowModesModelChange={(model) => this.setState({ rowModesModel: model })}
            onRowModesModelChange={
              this.props.onRowModesModelChange ||
              ((model) => this.setState({ rowModesModel: model }))
            }
            onProcessRowUpdateError={(err) => console.error(err)}
            showToolbar
            slots={{ toolbar: FGridToolbar as any, ...slots }}
            slotProps={{
              toolbar: {
                title,
                subtitle,
                addRow,
                saveAll: actualSaveAll,
                saving: this.state.saving,
                total: rows?.length || 0,
                dirtyCount: (rows || []).filter((r: any) => r._state && r._state !== 'clean').length,
                sx: toolbarSx,
                showQuickFilter,
                ...(slotProps as any)?.toolbar,
              },
              ...slotProps,
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f7fa',
                fontWeight: 700,
              },
              ...sx,
            }}
            {...rest}
          />
        </Box>
      </Paper>
    );
  }
}

export default FGrid;
